import React, {
    CSSProperties,
    Dispatch,
    SetStateAction,
    useEffect,
    useMemo,
    useState,
} from 'react';
import type { ActionMeta } from 'react-select';

import { FormikProps } from 'formik';

import { SearchableSelectType } from '../../../components/inputs/searchableSelect';
import VariantTypeDropdown from '../../../components/variant/VariantTypeDropdown';
// components
import styled from 'styled-components';

// theme
import aampeTheme from '../../../constants/aampeTheme';

import LabelsCreateAndSelectDropdown from '../../../editors/PushNotificationEditor/Sidebar/LabelsCreateAndSelectDropdown';

import useComponentTypesQuery from '../../../hooks/component-types/useComponentTypesQuery';
import useGetComponentTypeIdByVariant from '../../../hooks/component-types/useGetComponentTypeIdByVariant';
import useFormulaLabelsAddMutation from '../../../hooks/formulas/useFormulaLabelsAddMutation';
import useFormulaLabelsDeleteMutation from '../../../hooks/formulas/useFormulaLabelsDeleteMutation';
import useAnalytics from '../../../hooks/helper/useAnalytics';
import useLabelCreateMutation from '../../../hooks/labels/useLabelCreateMutation';
import useLabelsQuery from '../../../hooks/labels/useLabelsQuery';
import useRoleFlags from 'hooks/auth/useRoleFlags';
import useVariantsByMessageContentIdQuery from 'hooks/variants/useVariantsByMessageContentIdQuery';

import { FormulaLabel } from '../../../models/formula.model';

import { FormulaCreateType } from '../FormulaCreateMainV2';

// context
export interface FormulaLabelFormRow {
    variant_type: SearchableSelectType;
    labels: SearchableSelectType[];
}

interface FormulaLabelsRowProps {
    index: number;
    rowData: FormulaLabelFormRow;
    variantTypeOptions: SearchableSelectType[];
    inputList: FormulaLabelFormRow[];
    setInputList: Dispatch<SetStateAction<FormulaLabelFormRow[]>>;
    formulaId?: number;
    setVariantTypeOptions: any;
    createOrUpdateFormula?: FormulaCreateType;
    formikProps?: FormikProps<any>;
    customLabelStyle?: CSSProperties;
    variantTypePlaceholder?: string;
    isDisabled?: boolean;
}

const FormulaLabelsRow: React.FC<
    React.PropsWithChildren<FormulaLabelsRowProps>
> = ({
    index,
    rowData,
    variantTypeOptions,
    inputList,
    setInputList,
    formulaId,
    setVariantTypeOptions,
    createOrUpdateFormula,
    formikProps,
    customLabelStyle,
    variantTypePlaceholder,
    isDisabled,
}) => {
    const { data: componentTypes } = useComponentTypesQuery();
    const { hasWriteAccess } = useRoleFlags();

    const [selectedVariantType, setSelectedVariantType] = useState<string>(
        rowData.variant_type.value as string,
    );

    const { data: alternates } = useVariantsByMessageContentIdQuery(
        formulaId as number,
    );

    const labelsAlreadyInUse = useMemo<Set<number>>(() => {
        // loop through alternates match by currently selected variant type and make a set of labels to avoid duplication
        const labelsInUseSet = new Set<number>();
        alternates?.forEach((alternate) => {
            if (alternate.variant_type === selectedVariantType) {
                if (alternate.label) {
                    labelsInUseSet.add(alternate.label?.id);
                }
            }
        });
        return labelsInUseSet;
    }, [alternates, selectedVariantType]);

    const componentTypeId = useGetComponentTypeIdByVariant(selectedVariantType);

    const { data: labelsData } = useLabelsQuery(componentTypeId as string);

    const CreateLabelMutation = useLabelCreateMutation(
        rowData.variant_type.value as string,
    );
    const UpdateFormulaLabelsMutation = useFormulaLabelsAddMutation();
    const DeleteFormulaLabelsMutation = useFormulaLabelsDeleteMutation();

    const { track } = useAnalytics();

    const variantTypes = useMemo(
        () => componentTypes?.selectOptions ?? [],
        [componentTypes],
    );

    const labelOptions = useMemo(
        () =>
            labelsData
                ?.filter((label) => !labelsAlreadyInUse.has(label.id))
                .map((label) => ({
                    label: label.label_name,
                    value: label.id,
                })) || [],
        [labelsData, labelsAlreadyInUse],
    );

    const trackLabels = (
        action: 'removed' | 'added',
        newLabels: SearchableSelectType[],
    ) => {
        track(`formula_label_${action}`, {
            formula_id: formulaId,
            variant_type: selectedVariantType,
            current_labels: newLabels.map(
                (item: SearchableSelectType) => item.value,
            ),
        });
    };

    const deleteFormulaLabels = (
        labels: SearchableSelectType[],
        onSuccessCallback?: () => void,
    ) => {
        DeleteFormulaLabelsMutation.mutate(
            {
                id: formulaId!,
                data: {
                    labels: labels.map((label: SearchableSelectType) => ({
                        label_id: label.value,
                        variant_type: rowData.variant_type.value,
                    })),
                },
            },
            {
                onSuccess: () => onSuccessCallback?.(),
            },
        );
    };

    const formatFormulaLabels = (labels: SearchableSelectType[]) => {
        return labels.map((label: SearchableSelectType) => ({
            label_id: label.value,
            variant_type: rowData.variant_type.value as string,
        }));
    };

    const updateFormulaLabels = (labels: SearchableSelectType[]) => {
        if (!formulaId) {
            createOrUpdateFormula?.({
                labels: formatFormulaLabels(labels),
            });
        } else {
            UpdateFormulaLabelsMutation.mutate(
                {
                    id: formulaId,
                    labels: formatFormulaLabels(labels),
                },
                {
                    onSuccess: () => trackLabels('added', labels),
                },
            );
        }
    };

    const updateFormikPropsAndSubmit = (newLabels: SearchableSelectType[]) => {
        const variantType = rowData.variant_type.value;
        const existingLabels = formikProps?.values.labels || [];
        const oldValues = existingLabels.filter(
            (label: FormulaLabel) => label.variant_type === variantType,
        );
        if (newLabels.length > oldValues.length) {
            const newLabelToAdd = newLabels.find(
                (label) =>
                    !oldValues.some(
                        (oldValue: FormulaLabel) =>
                            oldValue.label_id === label.value,
                    ),
            );
            formikProps?.setFieldValue('labels', [
                ...existingLabels,
                {
                    label_id: newLabelToAdd?.value,
                    label_name: newLabelToAdd?.label,
                    variant_type: variantType,
                },
            ]);
        } else {
            const updatedLabels = existingLabels.filter(
                (label: FormulaLabel) =>
                    !(
                        newLabels.length === 0
                            ? oldValues
                            : oldValues.filter(
                                  (oldLabel: FormulaLabel) =>
                                      !newLabels.some(
                                          (newLabel) =>
                                              newLabel.value ===
                                              oldLabel.label_id,
                                      ),
                              )
                    ).some(
                        (filteredLabel: FormulaLabel) =>
                            filteredLabel.label_id === label.label_id,
                    ),
            );
            formikProps?.setFieldValue('labels', updatedLabels);
        }
    };

    const handleCreateOptionAction = (
        createdLabel: SearchableSelectType,
        value: SearchableSelectType[],
    ) => {
        CreateLabelMutation.mutate(
            {
                label_name: createdLabel.label,
                variant_type: rowData.variant_type.value as string,
            },
            {
                onSuccess: (res) => {
                    value.pop();
                    value.push({
                        value: res.data?.id,
                        label: createdLabel.label,
                    });
                    formikProps
                        ? updateFormikPropsAndSubmit(value)
                        : updateFormulaLabels(value);
                },
            },
        );
    };

    const handleFormulaLabelsChange = (
        name: string,
        value: SearchableSelectType[],
        actionMeta?: ActionMeta<SearchableSelectType>,
    ) => {
        if (!formikProps) {
            if (actionMeta?.action === 'remove-value') {
                const removedValue = actionMeta.removedValue;
                deleteFormulaLabels([removedValue], () =>
                    trackLabels('removed', value),
                );
            } else if (actionMeta?.action === 'clear') {
                const removedValues = actionMeta.removedValues;
                deleteFormulaLabels(
                    removedValues as SearchableSelectType[],
                    () => trackLabels('removed', []),
                );
            } else if (actionMeta?.action === 'create-option') {
                const createdLabel = actionMeta.option;
                handleCreateOptionAction(createdLabel, value);
            } else {
                updateFormulaLabels(value);
            }
        } else {
            if (actionMeta?.action === 'create-option') {
                const createdLabel = actionMeta.option;
                handleCreateOptionAction(createdLabel, value);
            } else {
                updateFormikPropsAndSubmit(value);
            }
        }
        setInputList((prevList) => {
            let list = [...prevList];
            list[index]['labels'] = value;
            return list;
        });
    };
    const handleVariantTypeChange = (
        name: string,
        value: SearchableSelectType,
    ) => {
        setSelectedVariantType(value.value as string);
        setInputList((prevList) => {
            let list = [...prevList];
            list[index]['variant_type'] = value;
            return list;
        });
    };

    useEffect(() => {
        setVariantTypeOptions(
            variantTypes.filter(
                (option) =>
                    !inputList
                        .map((item) => item.variant_type.value)
                        .includes(option.value),
            ),
        );
    }, [inputList, rowData.variant_type, setVariantTypeOptions, variantTypes]);

    //sort the selected labels displayed in editor
    const sortedLabels = React.useMemo(() => {
        return [...rowData.labels].sort((a, b) =>
            a.label.localeCompare(b.label),
        );
    }, [rowData.labels]);

    const LabelSelectDropdown = (
        <LabelsCreateAndSelectDropdown
            isMultiple
            name={'labels'}
            options={labelOptions}
            onChange={(name, value, actionMeta) =>
                handleFormulaLabelsChange(
                    name,
                    value as SearchableSelectType[],
                    actionMeta,
                )
            }
            value={sortedLabels}
            isDisabled={!rowData.variant_type.value || isDisabled}
            isCreatable={hasWriteAccess}
            placeholder={
                !rowData?.variant_type?.value
                    ? 'Select label type first'
                    : 'Select label'
            }
        />
    );

    const variantTypeDropDown = (
        <VariantTypeDropdown
            handleChange={handleVariantTypeChange}
            options={variantTypeOptions}
            selected={rowData.variant_type}
            isDisabled={!!rowData.labels.length || isDisabled}
            placeholder={variantTypePlaceholder}
        />
    );

    return (
        <RowContainer>
            <LabelType>
                <LabelTypeText style={customLabelStyle}>
                    Component type
                </LabelTypeText>
                {variantTypeDropDown}
            </LabelType>
            <LabelsDropdown>
                <LabelType>
                    <LabelTypeText style={customLabelStyle}>
                        Label name
                    </LabelTypeText>
                    {LabelSelectDropdown}
                </LabelType>
            </LabelsDropdown>
        </RowContainer>
    );
};

const RowContainer = styled.div(() => ({
    display: 'flex',
    gap: aampeTheme.spacing(2),
    alignItems: 'center',
    marginTop: aampeTheme.spacing(2),
    width: '100%',
}));

const LabelsDropdown = styled.div(() => ({
    minWidth: '200px',
    flexGrow: 1,
}));

const LabelType = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing(1),
});

const LabelTypeText = styled.span({
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '20px',
    color: aampeTheme.colors.gray800,
});
export default FormulaLabelsRow;
